import { Button } from '@repo/ui/components/ui/button'
import { Card } from '@repo/ui/components/ui/card'
import { cn } from '@repo/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import React, { useEffect, useState } from 'react'
import { handleAnchorClick } from '@/lib/utils/scroll'

export interface NavTarget {
  href?: string
  target?: React.HTMLAttributeAnchorTarget
  onClick?: (event?: React.MouseEvent<HTMLElement>) => void
}

export interface NavAction extends NavTarget {
  label: string
  variant?: React.ComponentProps<typeof Button>['variant']
  size?: React.ComponentProps<typeof Button>['size']
  disabled?: boolean
  loading?: boolean
  icon?: React.ReactNode
  className?: string
}

export interface SimpleNavLink extends NavTarget {
  type: 'simple'
  label: string
  description?: string
  icon?: React.ReactNode
}

export interface DropdownNavLink extends NavTarget {
  type: 'dropdown'
  label: string
  children: SimpleNavLink[]
}

export type NavLink = SimpleNavLink | DropdownNavLink

export interface BrandContent {
  name: string
  logo?: React.ReactNode
}

export interface NavigationContent {
  brand?: BrandContent
  links?: NavLink[]
  actions?: NavAction[]
  mobileBreakpoint?: number
  align?: 'left' | 'center' | 'right'
}

export interface NavLinksRendererProps {
  links: NavLink[]
  active: string | null
  setActive: (item: string | null) => void
  linkClassName?: string
  dropdownTriggerClassName?: string
  dropdownContentClassName?: string
  underline?: boolean
  linkVariant?: React.ComponentProps<typeof Button>['variant']
  linkSize?: React.ComponentProps<typeof Button>['size']
}

const dropdownTransition = {
  type: 'spring' as const,
  mass: 0.5,
  damping: 11.5,
  stiffness: 100,
  restDelta: 0.001,
  restSpeed: 0.001,
}

export const useIsBelowBreakpoint = (breakpoint: number) => {
  const [isBelow, setIsBelow] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false
    }
    return window.innerWidth < breakpoint
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleResize = () => {
      setIsBelow(window.innerWidth < breakpoint)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [breakpoint])

  return isBelow
}

interface NavButtonProps extends NavTarget {
  label: React.ReactNode
  icon?: React.ReactNode
  variant?: React.ComponentProps<typeof Button>['variant']
  size?: React.ComponentProps<typeof Button>['size']
  buttonClassName?: string
  childClassName?: string
  underline?: boolean
  disabled?: boolean
  loading?: boolean
}

const getRel = (target?: string) => (target === '_blank' ? 'noopener noreferrer' : undefined)

export const NavButton: React.FC<NavButtonProps> = ({
  label,
  icon,
  href,
  target,
  onClick,
  variant = 'ghost',
  size = 'default',
  buttonClassName,
  childClassName,
  underline = false,
  disabled = false,
  loading = false,
}) => {
  const rel = getRel(target)
  const neutralVariant = variant === 'ghost' || variant === 'link'

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event)

    if (href) {
      handleAnchorClick(event, href, { behavior: 'smooth' })
    }
  }

  const content = (
    <span className={cn('relative inline-flex items-center gap-2 leading-none', childClassName)}>
      {icon}
      <span>{label}</span>
      {underline && (
        <span className="pointer-events-none absolute -bottom-0.5 left-0 right-0 h-px origin-left scale-x-0 bg-current transition-transform duration-300 group-hover:scale-x-100" />
      )}
    </span>
  )

  const composedClassName = cn(
    'group font-medium transition-colors',
    neutralVariant && 'hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0',
    buttonClassName
  )

  return (
    <Button
      type={href ? undefined : 'button'}
      variant={variant}
      size={size}
      asChild={!!href}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={composedClassName}
    >
      {href ? (
        <NavAnchor href={href} target={target} rel={rel} className="inline-flex items-center">
          {content}
        </NavAnchor>
      ) : (
        content
      )}
    </Button>
  )
}

interface NavAnchorProps {
  href?: string
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
  children: React.ReactNode
  className?: string
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export const isInternalHref = (href?: string) => {
  if (!href) return false
  if (
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:')
  ) {
    return false
  }
  if (href.startsWith('//')) {
    return false
  }
  if (href.startsWith('#')) {
    return false
  }
  return href.startsWith('/')
}

export const resolveLinkProps = (href?: string, target?: string) => {
  const isInternal = isInternalHref(href) && !target
  if (isInternal && href && href.trim()) {
    return { type: 'internal' as const, to: href }
  }
  return { type: 'external' as const, href }
}

export const NavAnchor = React.forwardRef<HTMLAnchorElement, NavAnchorProps>(
  ({ href, target, rel, children, className, onClick }, ref) => {
    const link = resolveLinkProps(href, target)

    if (link.type === 'internal') {
      return (
        <Link
          to={link.to}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={className}
          onClick={onClick}
          preload="intent"
        >
          {children}
        </Link>
      )
    }

    return (
      <a href={href} target={target} rel={rel} className={className} ref={ref} onClick={onClick}>
        {children}
      </a>
    )
  }
)
NavAnchor.displayName = 'NavAnchor'

interface NavActionButtonProps {
  action: NavAction
  className?: string
}

export const NavActionButton: React.FC<NavActionButtonProps> = ({ action, className }) => {
  const {
    label,
    icon,
    variant,
    size = 'default',
    href,
    target,
    onClick,
    disabled,
    loading,
    className: actionClassName,
  } = action

  return (
    <NavButton
      label={label}
      icon={icon}
      href={href}
      target={target}
      onClick={onClick}
      variant={variant ?? 'default'}
      size={size}
      buttonClassName={cn('rounded-full px-4 py-2 text-base', actionClassName, className)}
      disabled={disabled}
      loading={loading}
    />
  )
}

export const NavLinksRenderer: React.FC<NavLinksRendererProps> = ({
  links,
  active,
  setActive,
  linkClassName,
  dropdownTriggerClassName,
  dropdownContentClassName,
  underline = true,
  linkVariant = 'ghost',
  linkSize = 'default',
}) => {
  return (
    <div className="flex items-center space-x-4">
      {links.map((link) => {
        if (link.type === 'simple') {
          return (
            <motion.div key={link.label} transition={{ duration: 0.3 }}>
              <NavButton
                label={link.label}
                icon={link.icon}
                href={link.href}
                target={link.target}
                onClick={link.onClick}
                underline={underline}
                variant={linkVariant}
                size={linkSize}
                buttonClassName={cn('px-3 py-2 text-base', linkClassName)}
              />
            </motion.div>
          )
        }

        return (
          <DropdownItem
            key={link.label}
            link={link}
            setActive={setActive}
            active={active}
            underline={underline}
            triggerClassName={dropdownTriggerClassName}
            linkVariant={linkVariant}
            linkSize={linkSize}
          >
            <div className={cn('py-2', dropdownContentClassName)}>
              {link.children.map((child) => (
                <NavButton
                  key={child.label}
                  label={child.label}
                  icon={child.icon}
                  href={child.href}
                  target={child.target}
                  onClick={child.onClick}
                  variant={linkVariant}
                  size={linkSize}
                  buttonClassName="w-full justify-start px-3 py-2 text-base text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  childClassName="justify-start"
                />
              ))}
            </div>
          </DropdownItem>
        )
      })}
    </div>
  )
}

const DropdownItem = ({
  link,
  setActive,
  active,
  children,
  underline,
  triggerClassName,
  linkVariant = 'ghost',
  linkSize = 'default',
}: {
  link: DropdownNavLink
  setActive: (item: string | null) => void
  active: string | null
  children?: React.ReactNode
  underline: boolean
  triggerClassName?: string
  linkVariant?: React.ComponentProps<typeof Button>['variant']
  linkSize?: React.ComponentProps<typeof Button>['size']
}) => {
  const { label, href, target, onClick } = link

  return (
    // biome-ignore lint/a11y/useSemanticElements: Complex dropdown component with intentional div structure
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => setActive(label)}
      onMouseLeave={() => setActive(null)}
      className="relative"
    >
      <motion.div transition={{ duration: 0.3 }}>
        <NavButton
          label={label}
          href={href}
          target={target}
          onClick={onClick}
          underline={underline}
          variant={linkVariant}
          size={linkSize}
          buttonClassName={cn('px-3 py-2 text-base', triggerClassName)}
        />
      </motion.div>
      {active !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={dropdownTransition}
        >
          {active === label && (
            <div className="absolute top-full left-0 pt-2 z-50">
              <motion.div transition={dropdownTransition} layoutId="active">
                <Card className="bg-background/98 backdrop-blur-md rounded-[6px] overflow-hidden border border-border/80 shadow-2xl ring-1 ring-border/30 max-w-48">
                  <motion.div layout className="w-full h-full px-4">
                    {children}
                  </motion.div>
                </Card>
              </motion.div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  )
}
